// Customer-facing text of every public error code of the LUD API, Arabic and English.
//
// The backend registry (lud-backend/errors.js + lud-backend/errorCatalog/*.js) owns the codes; the files in ./messages own
// their wording, one file per backend domain. Every registry code must have an entry: lud-backend/test/errorRegistry.test.js
// reads these files and fails on a missing or one-language entry, and errors.test.js fails on a code defined in two files.
// Codes the server never sends (NETWORK_ERROR, OFFLINE, INVALID_RESPONSE) come from the frontend normalizer (core.js).
//
// Wording rules: say what happened and what the user can do; no internal detail, no raw code. A `{name}` placeholder is
// filled from the error's `details` by errorText() (plain text, never HTML); a placeholder is used only where the backend
// sends that detail.
import core from "./messages/core";
import users from "./messages/users";
import stores from "./messages/stores";
import billing from "./messages/billing";
import aiWallet from "./messages/aiWallet";
import menu from "./messages/menu";
import ai from "./messages/ai";
import support from "./messages/support";
import workflow from "./messages/workflow";
import reporting from "./messages/reporting";
import deliveryDocumentation from "./messages/deliveryDocumentation";
import mail from "./messages/mail";
import database from "./messages/database";

export const ERROR_MESSAGE_SOURCES = Object.freeze({ core, users, stores, billing, aiWallet, menu, ai, support, workflow, reporting, deliveryDocumentation, mail, database });

// Later files never override earlier ones: a code in two files is reported by the test, and the first definition wins.
export const ERROR_MESSAGES = Object.freeze(
  Object.values(ERROR_MESSAGE_SOURCES).reduce((all, source) => {
    Object.entries(source).forEach(([code, entry]) => {
      if (!(code in all)) all[code] = Object.freeze(entry);
    });
    return all;
  }, {}),
);

// Fallback per error kind (see normalizeApiError): used for codes this catalog does not know.
export const KIND_FALLBACK = Object.freeze({
  network: "NETWORK_ERROR",
  offline: "OFFLINE",
  timeout: "REQUEST_TIMEOUT",
  unauthorized: "UNAUTHORIZED",
  forbidden: "FORBIDDEN",
  validation: "VALIDATION_FAILED",
  not_found: "NOT_FOUND",
  conflict: "CONFLICT",
  too_large: "PAYLOAD_TOO_LARGE",
  rate_limited: "RATE_LIMITED",
  unavailable: "SERVICE_UNAVAILABLE",
  server: "SERVER_ERROR",
  invalid: "INVALID_RESPONSE",
  unknown: "SERVER_ERROR",
});
